import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { CompanyStatusBadge } from '@l2u/ui/components/StatusBadge.tsx';
import { NavIcon, type IconName } from '@l2u/ui/components/NavIcon.tsx';
import { ProgressBar } from '@l2u/ui/components/ProgressBar.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { COMPANY_STATUS_LABELS, COMPANY_STATUSES } from '@l2u/ui/lib/companyStatus.ts';
import {
  matchesCompanyFilter,
  matchesCompanyQuery,
  parseCompanyFilter,
  parseStoredQuery,
  reconcileCompanyFilter,
  type CompanyFilter,
} from '../lib/companyFilter.ts';
import { companyFilterOptions } from '../lib/companyFilterOptions.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { clearStoredFilter, readStoredFilter, writeStoredFilter } from '../lib/filterStorage.ts';
import { dashboardListUrl } from '../lib/dashboardListUrl.ts';
import { vendorListName } from '../lib/vendorListName.ts';
import { SearchInput } from '../components/SearchInput.tsx';
import { complianceCounts } from '../lib/complianceProgress.ts';
import { progressColor } from '../lib/progressColor.ts';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import { companyInfoLabel } from '@l2u/ui/lib/companyInfo.ts';
import type { AdminCompanyListRow } from '@l2u/ui/lib/types.ts';
import type { CompanyStatus } from '@l2u/shared/schemas';

// Tiles: an "all" summary tile plus one per status (lifecycle order from the
// shared COMPANY_STATUSES). Labels also come from companyStatus.ts so the
// dashboard never drifts from the badges/filters elsewhere in the admin.
type Tile = 'all' | CompanyStatus;
const TILES: Tile[] = ['all', ...COMPANY_STATUSES];

type TileVisual = {
  icon: IconName;
  box: string; // tinted icon-box background
  fg: string; // icon foreground (inherited via currentColor)
};

// Dashboard-only presentation (icon + colour) per status. Colours mirror the
// status badge palette (StatusBadge.tsx) so tiles and badges stay aligned.
const STATUS_VISUAL: Record<CompanyStatus, TileVisual> = {
  invited: { icon: 'mail', box: 'bg-slate-100', fg: 'text-slate-600' },
  in_progress: { icon: 'clock', box: 'bg-blue-50', fg: 'text-blue-600' },
  review: { icon: 'eye', box: 'bg-amber-50', fg: 'text-amber-600' },
  changes: { icon: 'alert-triangle', box: 'bg-orange-50', fg: 'text-orange-600' },
  compliant: { icon: 'check-circle', box: 'bg-emerald-50', fg: 'text-emerald-600' },
  expired: { icon: 'x-circle', box: 'bg-red-50', fg: 'text-red-600' },
  deactivated: { icon: 'ban', box: 'bg-slate-200', fg: 'text-slate-500' },
};

// The "all" summary tile — neutral slate so it reads as an overview, not a status.
const ALL_VISUAL: TileVisual = {
  icon: 'building',
  box: 'bg-slate-100',
  fg: 'text-slate-600',
};

function tileVisual(t: Tile): TileVisual {
  return t === 'all' ? ALL_VISUAL : STATUS_VISUAL[t];
}

function tileLabel(t: Tile): string {
  return t === 'all' ? 'All vendors' : COMPANY_STATUS_LABELS[t];
}

const EMPTY_FILTER: CompanyFilter = { contractorTypes: [], clusterRegions: [], stations: [] };

const DASHBOARD_FILTERS_KEY = 'dashboard.filters';

export function AdminDashboard() {
  // Hydration is synchronous — seeded in the useState initializer, never
  // copied in from an effect — so the controls render preselected on the very
  // first paint (pinned by filterHydration.test.tsx).
  const [filter, setFilter] = useState<CompanyFilter>(
    () => readStoredFilter(DASHBOARD_FILTERS_KEY, parseCompanyFilter) ?? EMPTY_FILTER,
  );
  const [query, setQuery] = useState(
    () => readStoredFilter(DASHBOARD_FILTERS_KEY, parseStoredQuery) ?? '',
  );
  const navigate = useNavigate();

  const { settings, loading: settingsLoading } = usePortalSettings();
  const { user } = useAdminAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: AdminCompanyListRow[] }>('/api/companies'),
  });
  // All hooks must come before any early return so the call order is stable.
  // Deactivated companies are parked: they count under their own tile, but the
  // all-tile, the subtitle and the recent-activity list reflect only active
  // companies (mirrors the companies list, whose default view hides them —
  // deactivating bumps updated_at, which would otherwise pin the company to
  // the top of Recent activity).
  const activeCompanies = useMemo(
    () => (data?.companies ?? []).filter((c) => c.status !== 'deactivated'),
    [data],
  );

  // Apply the filter bar dimensions to the active company set.
  const filteredCompanies = useMemo(
    () =>
      activeCompanies.filter(
        (c) => matchesCompanyFilter(c, filter) && matchesCompanyQuery(c, query),
      ),
    [activeCompanies, filter, query],
  );

  // Same filter dimensions, but over ALL companies (incl. deactivated) so every
  // per-status tile reflects the filters. Status-exclusion and filtering
  // commute, so the non-deactivated slice of this equals filteredCompanies.
  const filteredAll = useMemo(
    () =>
      (data?.companies ?? []).filter(
        (c) => matchesCompanyFilter(c, filter) && matchesCompanyQuery(c, query),
      ),
    [data, filter, query],
  );

  // Settings-curated lists for all three dimensions, narrowed to the signed-in
  // user's scope where one applies — see companyFilterOptions for why cluster
  // region is neither scoped nor derived from the loaded rows. Deliberately
  // independent of `data`: the options are the configured lists, not what is
  // currently in use.
  const filterOptions = useMemo(() => companyFilterOptions(settings, user), [settings, user]);

  // Write-through: persist every change; an all-empty filter clears the key so
  // an explicit clear can never be resurrected on the next mount.
  useEffect(() => {
    const empty =
      filter.contractorTypes.length === 0 &&
      filter.clusterRegions.length === 0 &&
      filter.stations.length === 0 &&
      query === '';
    if (empty) clearStoredFilter(DASHBOARD_FILTERS_KEY);
    else writeStoredFilter(DASHBOARD_FILTERS_KEY, { ...filter, query });
  }, [filter, query]);

  // Reconcile restored values against the live option list — but only once
  // Settings has resolved (pruning against the empty loading-window list would
  // wipe every filter). Explicit rather than left to orderedSelected: the
  // pruned set flows back to storage via the write-through effect above, so a
  // restored dropdown can never show a badge count the panel doesn't have.
  // reconcileCompanyFilter additionally refuses to prune against an all-empty
  // option set (a failed settings fetch is indistinguishable from "nothing
  // configured") and returns the same reference when nothing changed, so this
  // can neither wipe a filter on a network blip nor loop.
  // The region options now come from the companies query, so pruning must also
  // wait for it — otherwise a stored region filter would be wiped during the
  // loading window, exactly as the Settings note above describes.
  useEffect(() => {
    if (settingsLoading || isLoading) return;
    setFilter((f) => reconcileCompanyFilter(f, filterOptions));
  }, [settingsLoading, isLoading, filterOptions]);

  if (isLoading) return <CenteredSpinner />;

  // Per-status counts come from the filtered set so every tile — including
  // Deactivated — reflects the active filters. "All vendors" mirrors the
  // subtitle: filtered and active-only (deactivated companies are parked).
  const counts: Record<CompanyStatus, number> = {
    invited: 0,
    in_progress: 0,
    review: 0,
    changes: 0,
    compliant: 0,
    expired: 0,
    deactivated: 0,
  };
  for (const c of filteredAll) counts[c.status] += 1;
  const tileCount = (t: Tile) => (t === 'all' ? filteredCompanies.length : counts[t]);

  const byRecent = (a: AdminCompanyListRow, b: AdminCompanyListRow) => (b.updated_at < a.updated_at ? -1 : 1);
  const recent = [...filteredCompanies].sort(byRecent).slice(0, 6);

  const listCompanies = recent;
  const count = listCompanies.length;

  const {
    compliant: compliantCount,
    total: complianceTotal,
    pct: compliancePct,
  } = complianceCounts(filteredCompanies);
  const filtersActive =
    filter.contractorTypes.length > 0 ||
    filter.clusterRegions.length > 0 ||
    filter.stations.length > 0 ||
    query.trim() !== '';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">
          {filtersActive
            ? `${filteredCompanies.length} of ${activeCompanies.length} vendor${activeCompanies.length === 1 ? '' : 's'} tracked`
            : `${activeCompanies.length} vendor${activeCompanies.length === 1 ? '' : 's'} tracked`}
        </p>
      </div>

      {/* Filters — kept directly under the heading so they govern everything
          below (tiles, progress, recent activity). Search + the three filter
          dropdowns sit on one row (mirrors the Vendors list), stacking on
          narrow viewports; the search box flexes to fill the leftover width. */}
      {/* Filter + compliance progress form one group spaced 20px apart —
          tighter than the page's default 32px rhythm. */}
      <div className="space-y-5">
        <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
          <SearchInput
            className="flex-1"
            value={query}
            onChange={setQuery}
            placeholder="Search name, legal name, contact, email, vendor #, contract # or territory…"
          />
          <FilterBar
            options={filterOptions}
            value={filter}
            onChange={setFilter}
            extraActive={query.trim() !== ''}
            onClearExtra={() => setQuery('')}
          />
        </div>

        {/* Compliance progress */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <ProgressBar
            value={compliancePct}
            label={`${compliantCount}/${complianceTotal} vendor${complianceTotal === 1 ? '' : 's'} compliant (${compliancePct}%)`}
            color={progressColor(compliancePct)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-8">
        {TILES.map((t) => {
          const meta = tileVisual(t);
          return (
            <Link
              key={t}
              to={dashboardListUrl(t, query, filter)}
              className="group flex flex-col rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300 hover:shadow-sm"
            >
              <div className="flex items-start justify-between">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-lg ${meta.box} ${meta.fg}`}
                >
                  <NavIcon name={meta.icon} size={18} />
                </span>
                <NavIcon
                  name="chevron-right"
                  size={16}
                  className="text-slate-300 transition group-hover:text-slate-400"
                />
              </div>
              <div className="mt-3 text-2xl font-semibold text-slate-900">{tileCount(t)}</div>
              <div className="text-sm text-slate-500">{tileLabel(t)}</div>
            </Link>
          );
        })}
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Recent activity
          </h2>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Vendor</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">{companyInfoLabel(settings, 'contractorType')}</th>
                <th className="px-4 py-2">{companyInfoLabel(settings, 'clusterRegion')}</th>
                <th className="px-4 py-2">{companyInfoLabel(settings, 'station')}</th>
                <th className="px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listCompanies.map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/vendors/${c.id}`)}
                >
                  <td className="px-4 py-2">
                    {/* Link kept for keyboard nav / right-click; row onClick handles
                        the primary pointer interaction across the full row. */}
                    <Link
                      to={`/vendors/${c.id}`}
                      className="font-medium text-slate-900 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* The legal name, not the trading name: the admin-entered
                          company_name IS the trading name (0070 dropped the
                          separate column on that reasoning). Shared with the
                          vendors list and the detail page's Legal entity name
                          row via vendorListName — registry name → vendor-entered
                          legal name → company_name so the cell is never blank. */}
                      {vendorListName(c.abn_entity_name, c.legal_entity_name, c.company_name)}
                    </Link>
                    <div className="text-xs text-slate-500">{c.contact_name}</div>
                  </td>
                  <td className="px-4 py-2">
                    <CompanyStatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {c.contractor_types && c.contractor_types.length > 0 ? (
                      c.contractor_types.join(', ')
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {c.cluster_regions && c.cluster_regions.length > 0 ? (
                      c.cluster_regions.join(', ')
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {c.stations && c.stations.length > 0 ? (
                      c.stations.join(', ')
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {new Date(c.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
              {count === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                    {filtersActive ? 'No vendors match the current filters.' : 'No vendors yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
